import React, { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, ArrowLeft, Mail } from "lucide-react";
import logoSvg from "./assets/logo.svg";

const COPY = {
  de: {
    backToSite: "Zur Startseite",
    ok: {
      eyebrow: "Danke fürs Bestätigen",
      title: "Du bist dabei.",
      body:
        "Deine Anmeldung zum Newsletter ist bestätigt. Wir melden uns, sobald es etwas Spannendes aus Lappland zu erzählen gibt — und versprechen: kein Spam.",
      hint:
        "Eine kurze Willkommens-E-Mail ist bereits unterwegs zu dir.",
    },
    already: {
      eyebrow: "Schon bestätigt",
      title: "Du warst schon dabei.",
      body:
        "Diese Anmeldung wurde bereits zuvor bestätigt. Es gibt nichts weiter zu tun — wir melden uns, sobald es Neues gibt.",
      hint: null,
    },
    expired: {
      eyebrow: "Link abgelaufen",
      title: "Der Bestätigungslink ist nicht mehr gültig.",
      body:
        "Aus Sicherheitsgründen verfallen unsere Bestätigungslinks nach 7 Tagen. Bitte melde dich erneut über das Formular auf der Startseite an — wir schicken dir dann einen frischen Link.",
      hint: null,
    },
    invalid: {
      eyebrow: "Link ungültig",
      title: "Diese Bestätigung kennen wir nicht.",
      body:
        "Der verwendete Link ist ungültig oder wurde bereits genutzt. Falls du dich gerade angemeldet hast, prüfe bitte erneut dein Postfach — möglicherweise gibt es eine neuere Bestätigungs-E-Mail.",
      hint: null,
    },
  },
  en: {
    backToSite: "Back to the site",
    ok: {
      eyebrow: "Thank you for confirming",
      title: "You’re in.",
      body:
        "Your newsletter subscription is confirmed. We’ll be in touch as soon as there’s something worth sharing from Lapland — and we promise: no spam.",
      hint: "A short welcome email is already on its way to you.",
    },
    already: {
      eyebrow: "Already confirmed",
      title: "You were already on the list.",
      body:
        "This subscription has already been confirmed. Nothing more to do — we’ll be in touch when there’s news worth sharing.",
      hint: null,
    },
    expired: {
      eyebrow: "Link expired",
      title: "This confirmation link is no longer valid.",
      body:
        "For security reasons, our confirmation links expire after 7 days. Please sign up again via the form on the home page and we’ll send you a fresh link.",
      hint: null,
    },
    invalid: {
      eyebrow: "Link not recognised",
      title: "We don’t recognise this confirmation.",
      body:
        "The link you used is invalid or has already been used. If you’ve just signed up, please check your inbox again — there may be a more recent confirmation email.",
      hint: null,
    },
  },
  sv: {
    backToSite: "Tillbaka till startsidan",
    ok: {
      eyebrow: "Tack för bekräftelsen",
      title: "Du är med.",
      body:
        "Din prenumeration på nyhetsbrevet är bekräftad. Vi hör av oss så snart det finns något spännande att berätta från Lappland — och vi lovar: inget skräp.",
      hint: "Ett kort välkomstmeddelande är redan på väg till dig.",
    },
    already: {
      eyebrow: "Redan bekräftad",
      title: "Du var redan med.",
      body:
        "Denna prenumeration har redan bekräftats. Inget mer behöver göras — vi hör av oss när det finns nyheter värda att dela.",
      hint: null,
    },
    expired: {
      eyebrow: "Länken har gått ut",
      title: "Bekräftelselänken är inte längre giltig.",
      body:
        "Av säkerhetsskäl går våra bekräftelselänkar ut efter 7 dagar. Anmäl dig gärna igen via formuläret på startsidan så skickar vi en ny länk.",
      hint: null,
    },
    invalid: {
      eyebrow: "Okänd länk",
      title: "Vi känner inte igen den här bekräftelsen.",
      body:
        "Länken du använt är ogiltig eller har redan använts. Om du just anmält dig, kontrollera din inkorg igen — det kan finnas ett nyare bekräftelsemeddelande.",
      hint: null,
    },
  },
};

const STATUSES = new Set(["ok", "already", "expired", "invalid"]);

export default function NewsletterConfirmed() {
  const [params] = useSearchParams();
  const rawLang = (params.get("lang") || "de").toLowerCase();
  const lang = ["de", "en", "sv"].includes(rawLang) ? rawLang : "de";
  const rawStatus = (params.get("status") || "ok").toLowerCase();
  const status = STATUSES.has(rawStatus) ? rawStatus : "ok";

  const copy = COPY[lang];
  const block = copy[status];
  const isSuccess = status === "ok" || status === "already";

  const Icon = isSuccess ? CheckCircle2 : AlertCircle;
  const accentClass = useMemo(() => {
    if (status === "ok") return "text-sage";
    if (status === "already") return "text-sage-dark";
    return "text-amber-700";
  }, [status]);

  return (
    <main
      data-testid={`newsletter-confirmed-${status}`}
      data-lang={lang}
      className="min-h-screen relative flex flex-col bg-cream text-ink"
    >
      <div
        aria-hidden
        className="absolute -top-32 right-[-160px] w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(156,175,136,0.45) 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-140px] left-[-120px] w-[400px] h-[400px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(213,197,185,0.4) 0%, transparent 70%)",
        }}
      />

      <header className="relative px-6 sm:px-10 pt-10">
        <Link
          to="/"
          data-testid="confirmed-logo-link"
          className="inline-flex items-center gap-3"
        >
          <img src={logoSvg} alt="DogCenter Lappland" className="h-12 w-auto" />
          <span className="font-sans tracking-[0.16em] text-[13px] uppercase font-semibold text-brand-olive">
            DogCenter <span className="text-brand-sage font-medium">Lappland</span>
          </span>
        </Link>
      </header>

      <section className="relative flex-1 flex items-center justify-center px-6 sm:px-10 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl w-full text-center"
        >
          <Icon
            className={`mx-auto h-14 w-14 ${accentClass}`}
            strokeWidth={1.25}
            aria-hidden
          />

          <div className="mt-8 eyebrow text-sage-dark inline-flex items-center justify-center">
            {block.eyebrow}
          </div>

          <h1
            data-testid="confirmed-title"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] tracking-tight text-ink"
          >
            {block.title}
          </h1>

          <p className="mt-7 text-ink/80 text-lg leading-relaxed font-light">
            {block.body}
          </p>

          {block.hint && (
            <div className="mt-8 inline-flex items-center gap-3 text-sm text-ink/65 bg-white/60 backdrop-blur rounded-full px-5 py-3 border border-sage/20">
              <Mail className="h-4 w-4 text-sage-dark" aria-hidden />
              <span>{block.hint}</span>
            </div>
          )}

          <div className="mt-12">
            <Link
              to="/"
              data-testid="confirmed-back-button"
              className="inline-flex items-center gap-2 rounded-full bg-brand-olive text-cream px-7 py-3 font-medium hover:bg-sage-dark transition-colors"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {copy.backToSite}
            </Link>
          </div>
        </motion.div>
      </section>

      <footer className="relative px-6 sm:px-10 pb-8 text-center">
        <p className="text-xs tracking-[0.18em] uppercase text-mutedfg/70">
          DogCenter Lappland AB · Kristineberg
        </p>
      </footer>
    </main>
  );
}
